import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

let client: SupabaseClient | null = null

export function getSupabase(): SupabaseClient | null {
  if (!url || !anonKey) return null
  if (!client) {
    client = createClient(url, anonKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      },
    })
  }
  return client
}

export function isSupabaseConfigured(): boolean {
  return Boolean(url && anonKey)
}

let authClient: SupabaseClient | null = null

/**
 * A Supabase client for the password-recovery landing page (/reset-password).
 *
 * It must read the recovery token Supabase appends to the redirect URL
 * (detectSessionInUrl), but it deliberately persists nothing to
 * localStorage and never auto-refreshes — the recovery session only needs to
 * live long enough for the visitor to set a new password, then the tab is
 * done. Kept separate from {@link getSupabase} (the anon tester-insert
 * client) so the marketing page never accidentally holds an auth session.
 *
 * flowType 'implicit' stops supabase-js from trying a PKCE code-exchange
 * (whose verifier lives on the device that *requested* the reset, not on
 * this site — recovery is inherently cross-device), so the page drives the
 * exchange explicitly via verifyOtp / the URL hash instead.
 */
export function getAuthSupabase(): SupabaseClient | null {
  if (!url || !anonKey) return null
  if (!authClient) {
    authClient = createClient(url, anonKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: true,
        flowType: 'implicit',
      },
    })
  }
  return authClient
}

export type TesterSubmission = {
  full_name: string
  email: string
  platform: 'android' | 'ios'
  user_agent?: string | null
  referrer?: string | null
}

export type SubmitResult =
  | { ok: true; alreadySignedUp: boolean }
  | { ok: false; reason: 'not_configured' | 'duplicate' | 'network' | 'unknown'; message: string }

export async function submitTester(payload: TesterSubmission): Promise<SubmitResult> {
  const sb = getSupabase()
  if (!sb) {
    return {
      ok: false,
      reason: 'not_configured',
      message:
        'Sign-ups are temporarily unavailable. Please email hello@bimauncle.com to join.',
    }
  }

  const { error } = await sb.from('internal_testers').insert(payload)

  if (!error) return { ok: true, alreadySignedUp: false }

  if (error.code === '23505') {
    return { ok: true, alreadySignedUp: true }
  }

  return {
    ok: false,
    reason: 'unknown',
    message: error.message || 'Could not save — please try again.',
  }
}
